import { useMemo, type ReactNode } from 'react';
import {
  Checkbox,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  type SelectChangeEvent,
  type SxProps,
  type Theme,
} from '@mui/material';
import type { LookupState, Option } from '../types';

type SeasonTeamMultiSelectProps = {
  lookups: LookupState;
  value: string[];
  onChange: (teamIds: string[]) => void;
  label?: string;
  allLabel?: string;
  teams?: Option[];
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  sx?: SxProps<Theme>;
  renderSelected?: (selected: string[]) => ReactNode;
};

export function SeasonTeamMultiSelect({
  lookups,
  value,
  onChange,
  label = 'Team',
  allLabel = 'All Teams',
  teams = lookups.teams,
  size = 'medium',
  fullWidth = true,
  disabled = false,
  multiple = true,
  sx,
  renderSelected,
}: SeasonTeamMultiSelectProps) {
  const seasonOrderById = useMemo(
    () => new Map(lookups.seasons.map((season, index) => [season.value, index])),
    [lookups.seasons],
  );
  const groupedTeams = useMemo(() => {
    const groups = new Map<string, { seasonId: string; label: string; teams: Option[] }>();
    teams.forEach((team) => {
      const seasonId = team.season_id || '';
      const seasonLabel = lookups.seasons.find((season) => season.value === seasonId)?.label || 'No Season';
      if (!groups.has(seasonId)) groups.set(seasonId, { seasonId, label: seasonLabel, teams: [] });
      groups.get(seasonId)!.teams.push(team);
    });
    return [...groups.values()]
      .map((group) => ({ ...group, teams: [...group.teams].sort((a, b) => a.label.localeCompare(b.label)) }))
      .sort((a, b) => {
        const aOrder = seasonOrderById.get(a.seasonId) ?? Number.MAX_SAFE_INTEGER;
        const bOrder = seasonOrderById.get(b.seasonId) ?? Number.MAX_SAFE_INTEGER;
        return aOrder - bOrder || a.label.localeCompare(b.label);
      });
  }, [lookups.seasons, seasonOrderById, teams]);

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const nextValue = event.target.value;
    onChange(typeof nextValue === 'string' ? nextValue.split(',') : nextValue);
  };

  const renderValue = (selected: string[]) => {
    if (selected.length === 0) return allLabel;
    if (selected.length === 1) return teams.find((team) => team.value === selected[0])?.label || selected[0];
    return `${selected.length} teams selected`;
  };

  const menuItems = groupedTeams.flatMap((group) => [
    <ListSubheader key={`season-${group.seasonId || 'none'}`}>{group.label}</ListSubheader>,
    ...group.teams.map((team) => (
      <MenuItem key={team.value} value={team.value}>
        {multiple && <Checkbox checked={value.includes(team.value)} />}
        {team.label}
      </MenuItem>
    )),
  ]);

  return (
    <FormControl fullWidth={fullWidth} size={size} disabled={disabled} sx={sx}>
      <InputLabel>{label}</InputLabel>
      {multiple ? (
        <Select
          multiple
          label={label}
          value={value}
          onChange={handleChange}
          renderValue={renderSelected || renderValue}
        >
          {menuItems}
        </Select>
      ) : (
        <Select
          label={label}
          value={value[0] || ''}
          onChange={(event) => onChange(event.target.value ? [event.target.value] : [])}
          renderValue={(selected) => teams.find((team) => team.value === selected)?.label || selected}
        >
          {menuItems}
        </Select>
      )}
    </FormControl>
  );
}
